import React, { useState, useRef, useEffect } from "react";

// TestimonialsSlider.jsx
// Self-contained, Tailwind-based testimonial carousel component.
// Replace avatar/icon imports with your own assets if needed.

const TESTIMONIALS = [
  {
    quote:
      "सरांच्या सखोल ज्योतिषज्ञानामुळे आमच्या कुटुंबाला नेहमीच अचूक आणि उपयुक्त मार्ग मिळाला. खूप आभारी आहोत.",
    name: "सौ. मंजिरी जोशी",
    role: "Renowned Research Scientist"
  },
  {
    quote:
      "Mr. Kelkar has been our family's trusted mentor for decades — accurate, insightful, and deeply practical.",
    name: "Govardhan Bihani",
    role: "Ahilyanagar"
  },
  {
    quote:
      "Consulting Suneel Kelkar Sir changed my life. His predictions and remedies brought clarity and positive change.",
    name: "Aditya Oke",
    role: "Renowned Graphologist & Vaastu Consultant"
  },
  {
    quote:
      "His gold/silver forecasts and market-cycle insights are consistently precise — invaluable for my investments.",
    name: "Market Client",
    role: "Investor"
  },
  {
    quote:
      "Accurate guidance on career, health, home and finances — always timely and practical.",
    name: "Mayur Datar",
    role: "Chartered Accountant"
  },
  {
    quote:
      "Clear, consistent, actionable market guidance — one of the biggest reasons I've stayed active in markets.",
    name: "Mr. Atul Vyas",
    role: "Senior Portfolio Management Consultant"
  },
  {
    quote:
      "Since 2006, Shri Suneel Kelkar's accurate predictions, practical remedies, and mentorship have profoundly shaped my life and career.",
    name: "Mr. Aashish Kulkarni",
    role: "Senior VP, Cyber Security Leader"
  }
];

export default function TestimonialsSlider({ className = "" }) {
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    // keep index in bounds
    if (index < 0) setIndex(TESTIMONIALS.length - 1);
    if (index >= TESTIMONIALS.length) setIndex(0);
  }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const prev = () => setIndex((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  const next = () => setIndex((i) => (i + 1) % TESTIMONIALS.length);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const diff = e.touches[0].clientX - touchStartX.current;
    // provide small threshold in px
    if (Math.abs(diff) > 50) {
      if (diff > 0) prev();
      else next();
      touchStartX.current = null;
    }
  };
  const handleTouchEnd = () => {
    touchStartX.current = null;
  };

  return (
    <section className={`w-full max-w-4xl mx-auto relative ${className}`} aria-label="Testimonials">
      {/* Prev button */}
      <button
        onClick={prev}
        aria-label="Previous testimonial"
        className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow focus:outline-none z-20 ml-2"
      >
        {/* simple chevron left */}
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M12.293 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L8.414 10l3.879 3.879a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Track */}
      <div
        ref={trackRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="overflow-hidden"
      >
        <div
          style={{ transform: `translateX(-${index * 100}%)`, transition: "transform 0.35s ease-in-out" }}
          className="flex w-full"
        >
          {TESTIMONIALS.map((t, i) => (
            <article key={i} className="flex-shrink-0 w-full p-6 md:p-10">
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-md h-full flex flex-col justify-between">
                <blockquote className="text-lg md:text-xl leading-relaxed mb-4" aria-live={i === index ? "polite" : "off"}>
                  “{t.quote}”
                </blockquote>
                <div>
                  <cite className="font-semibold block">{t.name}</cite>
                  <p className="text-sm text-gray-600">{t.role}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Next button */}
      <button
        onClick={next}
        aria-label="Next testimonial"
        className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow focus:outline-none z-20 mr-2"
      >
        {/* simple chevron right */}
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M7.707 4.293a1 1 0 010 1.414L3.414 10l4.293 4.293a1 1 0 01-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 0z" clipRule="evenodd" transform="translate(10,0) rotate(180)" />
        </svg>
      </button>

      {/* Dots */}
      <div className="mt-4 flex justify-center gap-2">
        {TESTIMONIALS.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to testimonial ${i + 1}`}
            className={`w-3 h-3 rounded-full ${i === index ? "bg-gray-800" : "bg-gray-300"}`}
          />
        ))}
      </div>
    </section>
  );
}
